import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import "./AddReviewerManually.css";

const AddReviewer = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    domain1: "",
    domain2: "",
    domain3: "",
  });
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDomains, setLoadingDomains] = useState(true);

  // Fetch domains from database
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/get-domains`
        );
        setDomains(response.data);
        setLoadingDomains(false);
      } catch (error) {
        console.error("Error fetching domains:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load domains. Please refresh the page.",
          confirmButtonColor: '#5e71f6',
        });
        setLoadingDomains(false);
      }
    };
    fetchDomains();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: At least domain1 must be selected
    if (!formData.domain1) {
      Swal.fire({
        icon: "warning",
        title: "Domain Required",
        text: "Please select at least one expertise domain (Domain 1 is required).",
        confirmButtonColor: '#5e71f6',
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/admin/add-reviewer`,
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          domain1: formData.domain1,
          domain2: formData.domain2 || null,
          domain3: formData.domain3 || null,
        }
      );
      
      setLoading(false);

      if (response.status === 200) {
        Swal.fire({
          icon: "success",
          title: "Reviewer Added Successfully",
          text: `The reviewer has been successfully added. A login password has been sent to ${formData.email}`,
          confirmButtonColor: '#5e71f6',
        });
        
        // Clear form data
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          domain1: "",
          domain2: "",
          domain3: "",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.message,
          confirmButtonColor: '#5e71f6',
        });
      }
    } catch (error) {
      setLoading(false);
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Something went wrong. Please try again later.",
        confirmButtonColor: '#5e71f6',
      });
    }
  };

  return (
    <div className="add-reviewer-wrapper">
      <div className="add-reviewer-container">
        <div className="header-section">
          <h1 className="page-title">Add Reviewer</h1>
          <p className="page-subtitle">Add a new reviewer with expertise domains to the system</p>
        </div>

        <form onSubmit={handleSubmit} className="reviewer-form">
          {/* Name Fields */}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                First Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                placeholder="Enter first name"
                value={formData.firstName}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName" className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Last Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                placeholder="Enter last name"
                value={formData.lastName}
                onChange={handleChange}
                required
                className="form-input"
              />
            </div>
          </div>

          {/* Email */}
          <div className="form-group">
            <label htmlFor="email" className="form-label">
              <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter email address"
              value={formData.email}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          {/* Expertise Domains */}
          <div className="domains-section">
            <h3 className="section-title">
              <svg className="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Expertise Domains
            </h3>

            {/* Domain 1 - Required */}
            <div className="form-group">
              <label htmlFor="domain1" className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Primary Domain <span className="required">*</span>
              </label>
              <select
                id="domain1"
                name="domain1"
                value={formData.domain1}
                onChange={handleChange}
                required
                className="form-select"
                disabled={loadingDomains}
              >
                <option value="">
                  {loadingDomains ? "Loading domains..." : "Select primary domain"}
                </option>
                {domains.map((domain) => (
                  <option key={domain._id} value={domain.mainDomain}>
                    {domain.mainDomain}
                  </option>
                ))}
              </select>
            </div>

            {/* Domain 2 - Optional */}
            <div className="form-group">
              <label htmlFor="domain2" className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Secondary Domain <span className="optional-label">(Optional)</span>
              </label>
              <select
                id="domain2"
                name="domain2"
                value={formData.domain2}
                onChange={handleChange}
                className="form-select"
                disabled={loadingDomains}
              >
                <option value="">
                  {loadingDomains ? "Loading domains..." : "Select secondary domain (optional)"}
                </option>
                {domains.map((domain) => (
                  <option 
                    key={domain._id} 
                    value={domain.mainDomain}
                    disabled={formData.domain1 === domain.mainDomain}
                  >
                    {domain.mainDomain}
                  </option>
                ))}
              </select>
            </div>

            {/* Domain 3 - Optional */}
            <div className="form-group">
              <label htmlFor="domain3" className="form-label">
                <svg className="label-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                Tertiary Domain <span className="optional-label">(Optional)</span>
              </label>
              <select
                id="domain3"
                name="domain3"
                value={formData.domain3}
                onChange={handleChange}
                className="form-select"
                disabled={loadingDomains}
              >
                <option value="">
                  {loadingDomains ? "Loading domains..." : "Select tertiary domain (optional)"}
                </option>
                {domains.map((domain) => (
                  <option 
                    key={domain._id} 
                    value={domain.mainDomain}
                    disabled={formData.domain1 === domain.mainDomain || formData.domain2 === domain.mainDomain}
                  >
                    {domain.mainDomain}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Info Box */}
          <div className="info-box">
            <svg className="info-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="info-content">
              <strong>Note:</strong> A temporary password will be automatically generated and sent to the reviewer's email address for login.
            </div>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={loading || loadingDomains} className="submit-btn">
            {loading ? (
              <>
                <div className="loading-spinner-small"></div>
                Adding Reviewer...
              </>
            ) : (
              <>
                <svg className="btn-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
                Add Reviewer
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddReviewer;